import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { reportApiClientFailureFromAxios } from '../utils/clientOperationAnalytics';
import { resolveRequestAccessToken } from './authTokenForRequest';
import { dashboardCache } from './dashboardCache';
import type {
  MemoryReadSummaryResponse,
  MemoryReadTimeRange,
} from './memoryReadSummary';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

let lastApiRequestId: string | null = null;

function captureRequestIdFromHeaders(
  headers: Record<string, unknown> | undefined
): void {
  if (!headers) {
    return;
  }
  const raw = (headers['x-request-id'] ?? headers['X-Request-ID']) as
    | string
    | undefined;
  if (typeof raw === 'string' && raw.length > 0) {
    lastApiRequestId = raw;
  }
}

/** Last API `X-Request-ID` or JSON `request_id` from the most recent response (for support correlation). */
export function getLastApiRequestId(): string | null {
  return lastApiRequestId;
}

export interface Memory {
  id: string;
  content: string;
  tags: string[];
  source?: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
  space_id?: string;
  space_name?: string;
  importance?: number;
  category?: string;
  metadata?: Record<string, unknown>;
}

export interface MemoryCreate {
  content: string;
  tags?: string[];
  source?: string;
  importance?: number;
  metadata?: Record<string, any>;
}

export interface MemoryQuery {
  query: string;
  limit?: number;
  filter_tags?: string[];
  filter_source?: string;
  min_importance?: number;
}

export interface MemoryList {
  items: Memory[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/** Slim memory row for Memory Workspace map (no body text). */
export interface MemoryMapNode {
  id: string;
  created_at: string;
  updated_at?: string;
  importance?: number;
  category?: string;
  tags: string[];
  source?: string;
  label?: string | null;
  is_chunk: boolean;
  parent_memory_id?: string | null;
  total_chunks?: number | null;
}

export interface MemoryMapNodesPage {
  items: MemoryMapNode[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface MemoryMapTagCount {
  tag: string;
  count: number;
}

export interface MemoryMapSummaryMeta {
  computed_at: string;
  sort_basis: string;
  tag_sample_limit: number;
  approximate_counts: boolean;
}

export interface MemoryMapSummary {
  space_id: string;
  total_nodes: number;
  total_raw_objects?: number | null;
  top_tags: MemoryMapTagCount[];
  chunks_excluded_count?: number | null;
  meta: MemoryMapSummaryMeta;
}

export interface GetMemoryMapNodesParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  source?: string;
  minImportance?: number;
  excludeChunks?: boolean;
}

export interface GetMemoryMapSummaryParams {
  excludeChunks?: boolean;
  tags?: string[];
  source?: string;
  minImportance?: number;
  tagSampleLimit?: number;
}

/** Parameters for paginated GET /memories (tenant or space-scoped). */
export interface ListMemoriesPageParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  source?: string;
  minImportance?: number;
  /** When set, lists memories for this space (same as legacy getAllMemoriesInSpace). */
  spaceId?: string;
}

export interface MemoryImportItem {
  content: string;
  tags?: string[];
  source?: string;
  importance?: number;
  category?: string;
  metadata?: Record<string, any>;
}

export interface MemoryExportResponse {
  version: string;
  exported_at: string;
  total: number;
  space_id?: string;
  memories: MemoryImportItem[];
}

export interface MemoryImportResponse {
  imported: number;
  failed: number;
  errors: string[];
  space_id: string;
}

/** GET /api/v1/activity — durable activity feed (Postgres, dual-written with PostHog). */
export interface UserActivityFeedItem {
  id: string;
  user_id: string;
  space_id?: string | null;
  event_type: string;
  source?: string | null;
  integration_type?: string | null;
  title: string;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

export interface UserActivityFeedResponse {
  items: UserActivityFeedItem[];
  next_offset?: number | null;
  next_cursor?: string | null;
}

/** GET /api/v1/gamification — server-authoritative badges, XP, streaks. */
export interface GamificationAchievementApi {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  tier: string;
  requirement: { type: string; value: number };
  progress: number;
  isUnlocked: boolean;
  unlockedAt?: string | null;
  rarity: string;
}

export interface GamificationMilestoneApi {
  id: string;
  title: string;
  description: string;
  icon: string;
  value: number;
  isReached: boolean;
  reachedAt?: string | null;
  category: string;
}

export interface GamificationActivitySummaryApi {
  todayActivities: number;
  weekActivities: number;
  totalActivities: number;
  currentStreak: number;
  unlockedAchievements: number;
  reachedMilestones: number;
}

export interface GamificationUserStatsApi {
  totalMemories: number;
  totalSearches: number;
  totalSpaces: number;
  currentStreak: number;
  longestStreak: number;
  timeSpentSaving: number;
  productivityScore: number;
  level: number;
  experience: number;
  nextLevelExp: number;
  joinedDate: string;
  lastActiveDate: string;
}

export interface GamificationStateResponse {
  userStats: GamificationUserStatsApi;
  achievements: GamificationAchievementApi[];
  milestones: GamificationMilestoneApi[];
  activitySummary: GamificationActivitySummaryApi;
}

/** GET /api/v1/activity/weekly-summary — space-scoped Storage vs Retrieval counts. */
export interface WeeklyMemoryActivitySummary {
  storage_this_week: number;
  retrieval_this_week: number;
  window_days: number;
}

export interface AudioMemoryCreateParams {
  audioFile: Blob | File;
  /** Filename for the part (e.g. recording.webm) — required for Blob uploads */
  filename?: string;
  content: string;
  tags?: string[];
  source?: string;
  importance?: number;
  category?: string;
  spaceId?: string;
}

export interface SpaceCreate {
  name: string;
  icon?: string;
  color?: string;
  template_type?: string;
  privacy_level?: string;
  space_type?: 'text_only' | 'multimodal'; // Space type: text_only (default) or multimodal
}

export interface Space {
  id: string;
  space_id: string;
  name: string;
  icon: string;
  color: string;
  template_type: string;
  privacy_level: string;
  role: string;
  created_at: string;
  updated_at?: string;
  memory_count: number;
}

export interface SpaceList {
  spaces: Space[];
  total_count: number;
  space_limit: number;
}

export interface SpaceUpdate {
  name?: string;
  icon?: string;
  color?: string;
  privacy_level?: string;
}

// Socket-related interfaces
export interface SpacePermissions {
  can_read: boolean;
  can_write: boolean;
  can_search: boolean;
  can_delete: boolean;
  filters?: {
    categories?: string[];
    exclude_tags?: string[];
    importance_min?: number;
  };
  rate_limits?: {
    requests_per_minute?: number;
    requests_per_hour?: number;
    requests_per_day?: number;
  };
}

export interface SocketTrigger {
  event: string;
  conditions?: Array<Record<string, any>>;
}

export interface SocketAction {
  type: string;
  parameters: Record<string, any>;
  condition?: Record<string, any>;
}

export interface SocketConfiguration {
  triggers: SocketTrigger[];
  actions: SocketAction[];
  error_handling?: Record<string, any>;
  execution_mode: string;
  priority: number;
  template_id?: string;
  template_name?: string;
}

export interface SpaceSocketCreate {
  socket_name: string;
  description?: string;
  socket_config: SocketConfiguration;
}

export interface SpaceSocket {
  socket_id: string;
  space_id: string;
  user_id: string;
  socket_name: string;
  description?: string;
  socket_config: SocketConfiguration;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  integration_bindings: IntegrationBinding[];
}

export interface SocketList {
  sockets: SpaceSocket[];
  total_count: number;
  space_id: string;
}

export interface IntegrationBindingCreate {
  integration_type: string;
  integration_name: string;
  permissions: SpacePermissions;
  auth_config: Record<string, any>;
}

export interface IntegrationBinding {
  binding_id: string;
  socket_id: string;
  integration_type: string;
  integration_name: string;
  permissions: SpacePermissions;
  auth_config: Record<string, any>;
  is_active: boolean;
  status?: 'pending' | 'active' | 'inactive';
  created_at: string;
  last_used?: string;
  access_token_preview?: string;
}

export interface IntegrationBindingList {
  bindings: IntegrationBinding[];
  total_count: number;
  socket_id: string;
}

export interface IntegrationBindingUpdate {
  integration_name?: string;
  permissions?: SpacePermissions;
  auth_config?: Record<string, any>;
  is_active?: boolean;
}

export interface ApiError {
  detail: string;  // Backend uses 'detail' field for error messages (not 'message')
  status: number;
}

// Chrome Extension Generation Interfaces
export interface ExtensionConfig {
  name: string;
  description?: string;
  icon: string;
  theme_color: string;
}

export interface ExtensionGenerateRequest {
  socket_id: string;
  extension_config: ExtensionConfig;
}

export interface GeneratedExtension {
  filename: string;
  download_url: string;
  size: string;
  extension_id?: string;
  expires_at?: string;
}

export interface ExtensionGenerateResponse {
  success: boolean;
  extension?: GeneratedExtension;
  message: string;
  generation_time_ms?: number;
}

class ApiService {
  private client: AxiosInstance;
  private authToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000, // 10 seconds - should be enough for most operations
      headers: {
        'Content-Type': 'application/json',
        'X-Memara-Client': 'ui', // Identify requests from the UI
      },
    });

    // Request interceptor: prefer Supabase session token so requests never rely on a stale
    // in-memory token (fixes 401s when the UI session exists but apiService.authToken was not set yet).
    // getSession is capped; a hung Web Lock must not block the XHR until the axios timeout.
    this.client.interceptors.request.use(async (config) => {
      const lookup = await resolveRequestAccessToken(this.authToken, {
        url: config.url,
        method: config.method,
      });
      const token = lookup.token;
      if (token) {
        this.authToken = token;
        config.headers.Authorization = `Bearer ${token}`;
        console.log('🚀 API Request with auth:', {
          url: config.url,
          method: config.method,
          hasToken: true,
          sessionLookupMs: lookup.elapsedMs,
          sessionTimedOut: lookup.timedOut,
        });
      } else {
        console.log('⚠️ API Request without auth token:', {
          url: config.url,
          method: config.method,
          sessionLookupMs: lookup.elapsedMs,
          sessionTimedOut: lookup.timedOut,
        });
      }
      return config;
    });

    // Response interceptor for error handling and request-id capture (support / PostHog context)
    this.client.interceptors.response.use(
      (response) => {
        captureRequestIdFromHeaders(response.headers as Record<string, unknown>);
        return response;
      },
      (error) => {
        const h = error.response?.headers as Record<string, unknown> | undefined;
        captureRequestIdFromHeaders(h);
        const d = error.response?.data;
        if (
          d &&
          typeof d === 'object' &&
          d !== null &&
          'request_id' in d &&
          typeof (d as { request_id?: unknown }).request_id === 'string'
        ) {
          lastApiRequestId = (d as { request_id: string }).request_id;
        }
        if (error.response?.status === 401) {
          this.removeAuthToken();
          // Optionally redirect to login
        }
        reportApiClientFailureFromAxios(error, undefined, lastApiRequestId);
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string) {
    this.authToken = token;
  }

  removeAuthToken() {
    this.authToken = null;
  }

  /** POST with an explicit Bearer token (e.g. onboarding flows that already resolved the session). */
  async postWithAuth<T>(path: string, body: unknown, bearerToken: string): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(path, body, {
      headers: { Authorization: `Bearer ${bearerToken}` },
    });
    return response.data;
  }

  // Health check
  async getHealth(): Promise<any> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // Memory operations
  async createMemory(memory: MemoryCreate): Promise<Memory> {
    const response: AxiosResponse<Memory> = await this.client.post('/memories', memory);
    
    // Invalidate dashboard cache when memory is created
    dashboardCache.onMemoryChange();
    
    return response.data;
  }

  async createMemoryInSpace(memory: MemoryCreate, spaceId: string): Promise<Memory> {
    const response: AxiosResponse<Memory> = await this.client.post(`/memories?space_id=${spaceId}`, memory);
    
    // Invalidate dashboard cache when memory is created
    dashboardCache.onMemoryChange();
    
    return response.data;
  }

  /**
   * Multipart upload to POST /memories/audio (Whisper transcription + memory create).
   * Uses extended timeout; omits JSON Content-Type so the boundary is set correctly.
   */
  async createAudioMemory(params: AudioMemoryCreateParams): Promise<Memory> {
    const formData = new FormData();
    const name =
      params.filename ??
      (params.audioFile instanceof File ? params.audioFile.name : 'audio.webm');
    formData.append('audio_file', params.audioFile, name);
    formData.append('content', params.content);
    if (params.tags?.length) {
      formData.append('tags', params.tags.join(','));
    }
    if (params.source) {
      formData.append('source', params.source);
    }
    if (params.importance !== undefined) {
      formData.append('importance', String(params.importance));
    }
    if (params.category) {
      formData.append('category', params.category);
    }
    if (params.spaceId) {
      formData.append('space_id', params.spaceId);
    }

    const response: AxiosResponse<Memory> = await this.client.post(
      '/memories/audio',
      formData,
      {
        timeout: 180_000,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        transformRequest: [
          (data, headers) => {
            if (data instanceof FormData) {
              delete headers['Content-Type'];
            }
            return data;
          },
        ],
      }
    );

    dashboardCache.onMemoryChange();
    return response.data;
  }

  async searchMemories(query: MemoryQuery): Promise<Memory[]> {
    const response: AxiosResponse<Memory[]> = await this.client.post('/memories/search', query);
    return response.data;
  }

  /**
   * Paginated list; returns full MemoryList (items, total, page, pages).
   */
  async listMemoriesPage(options: ListMemoriesPageParams = {}): Promise<MemoryList> {
    const {
      page = 1,
      size = 100,
      sortBy = 'created_at',
      sortOrder = 'desc',
      tags,
      source,
      minImportance,
      spaceId,
    } = options;
    const params: Record<string, unknown> = {
      page,
      size,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    if (spaceId) params.space_id = spaceId;
    if (tags && tags.length > 0) params.tags = tags;
    if (source) params.source = source;
    if (minImportance !== undefined) {
      params.min_importance = minImportance;
    }
    const response: AxiosResponse<MemoryList> = await this.client.get('/memories', {
      params,
    });
    return response.data;
  }

  /**
   * Memory map headline stats (no memory body text).
   */
  async getMemoryMapSummary(
    spaceId: string,
    options: GetMemoryMapSummaryParams = {}
  ): Promise<MemoryMapSummary> {
    const {
      excludeChunks = true,
      tags,
      source,
      minImportance,
      tagSampleLimit,
    } = options;
    const params: Record<string, unknown> = {
      exclude_chunks: excludeChunks,
    };
    if (tags && tags.length > 0) params.tags = tags;
    if (source) params.source = source;
    if (minImportance !== undefined) params.min_importance = minImportance;
    if (tagSampleLimit !== undefined) params.tag_sample_limit = tagSampleLimit;
    const response: AxiosResponse<MemoryMapSummary> = await this.client.get(
      `/spaces/${encodeURIComponent(spaceId)}/memory-map/summary`,
      { params }
    );
    return response.data;
  }

  /**
   * Paginated memory map nodes (facets only; use getMemoryById for full text).
   */
  async getMemoryMapNodes(
    spaceId: string,
    options: GetMemoryMapNodesParams = {}
  ): Promise<MemoryMapNodesPage> {
    const {
      page = 1,
      size = 50,
      sortBy = 'created_at',
      sortOrder = 'desc',
      tags,
      source,
      minImportance,
      excludeChunks = true,
    } = options;
    const params: Record<string, unknown> = {
      page,
      size,
      sort_by: sortBy,
      sort_order: sortOrder,
      exclude_chunks: excludeChunks,
    };
    if (tags && tags.length > 0) params.tags = tags;
    if (source) params.source = source;
    if (minImportance !== undefined) params.min_importance = minImportance;
    const response: AxiosResponse<MemoryMapNodesPage> = await this.client.get(
      `/spaces/${encodeURIComponent(spaceId)}/memory-map/nodes`,
      { params }
    );
    return response.data;
  }

  async getAllMemories(options?: {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    tags?: string[];
    source?: string;
    minImportance?: number;
    size?: number;
  }): Promise<Memory[]> {
    const list = await this.listMemoriesPage({
      page: 1,
      size: options?.size ?? 100,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
      tags: options?.tags,
      source: options?.source,
      minImportance: options?.minImportance,
    });
    return list.items;
  }

  async getMemoryById(memoryId: string, spaceId?: string): Promise<Memory> {
    const params = spaceId ? { space_id: spaceId } : {};
    const response: AxiosResponse<Memory> = await this.client.get(`/memories/${memoryId}`, { params });
    return response.data;
  }

  async updateMemory(memoryId: string, memory: Partial<MemoryCreate>, spaceId?: string): Promise<Memory> {
    const params = spaceId ? { space_id: spaceId } : {};
    const response: AxiosResponse<Memory> = await this.client.patch(`/memories/${memoryId}`, memory, { params });
    
    // Invalidate dashboard cache when memory is updated
    dashboardCache.onMemoryChange();
    
    return response.data;
  }

  async deleteMemory(memoryId: string, spaceId?: string): Promise<{ success: boolean; message: string }> {
    const params = spaceId ? { space_id: spaceId } : {};
    const response = await this.client.delete(`/memories/${memoryId}`, { params });
    
    // Invalidate dashboard cache when memory is deleted
    dashboardCache.onMemoryChange();
    
    return response.data;
  }

  async exportMemories(spaceId?: string): Promise<MemoryExportResponse> {
    const params = spaceId ? { space_id: spaceId } : {};
    const response: AxiosResponse<MemoryExportResponse> = await this.client.get('/memories/export', { params });
    return response.data;
  }

  async importMemories(
    memories: MemoryImportItem[],
    spaceId?: string
  ): Promise<MemoryImportResponse> {
    const response: AxiosResponse<MemoryImportResponse> = await this.client.post('/memories/import', {
      memories,
      space_id: spaceId
    });
    dashboardCache.onMemoryChange();
    return response.data;
  }

  // Space operations
  async createSpace(space: SpaceCreate): Promise<Space> {
    const response: AxiosResponse<Space> = await this.client.post('/spaces', space, {
      timeout: 45000,
    });
    return response.data;
  }

  async getAllSpaces(): Promise<SpaceList> {
    const response: AxiosResponse<SpaceList> = await this.client.get('/spaces', {
      timeout: 45000,
    });
    return response.data;
  }

  async getSpace(spaceId: string): Promise<Space> {
    const response: AxiosResponse<Space> = await this.client.get(`/spaces/${spaceId}`);
    return response.data;
  }

  async updateSpace(spaceId: string, space: Partial<SpaceCreate>): Promise<Space> {
    const response: AxiosResponse<Space> = await this.client.put(`/spaces/${spaceId}`, space);
    return response.data;
  }

  async deleteSpace(spaceId: string, confirm: boolean = true): Promise<{ message: string; deleted_memories: number }> {
    const response: AxiosResponse<{ message: string; deleted_memories: number }> = await this.client.delete(`/spaces/${spaceId}?confirm=${confirm}`);
    return response.data;
  }

  async touchSpace(spaceId: string): Promise<void> {
    await this.client.post(`/spaces/${spaceId}/access`);
  }

  // Space-aware memory operations
  async searchMemoriesInSpace(query: MemoryQuery, spaceId?: string): Promise<Memory[]> {
    const params = spaceId ? { space_id: spaceId } : {};
    const response: AxiosResponse<Memory[]> = await this.client.post('/memories/search', query, { params });
    return response.data;
  }

  async getAllMemoriesInSpace(spaceId?: string, options?: {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    tags?: string[];
    source?: string;
    minImportance?: number;
    size?: number;
  }): Promise<Memory[]> {
    const list = await this.listMemoriesPage({
      page: 1,
      size: options?.size ?? 100,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
      tags: options?.tags,
      source: options?.source,
      minImportance: options?.minImportance,
      spaceId,
    });
    return list.items;
  }

  // User operations
  async getCurrentUser(): Promise<any> {
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  // Socket operations
  async createSocket(spaceId: string, socket: SpaceSocketCreate): Promise<SpaceSocket> {
    const response: AxiosResponse<SpaceSocket> = await this.client.post(`/api/v1/spaces/${spaceId}/sockets`, socket);
    return response.data;
  }

  async createSocketFromTemplate(
    spaceId: string, 
    socketName: string, 
    description: string, 
    templateId: string, 
    templateConfig?: any
  ): Promise<SpaceSocket> {
    const requestData = {
      socket_name: socketName,
      description: description,
      template_id: templateId,
      template_config: templateConfig
    };
    const response: AxiosResponse<SpaceSocket> = await this.client.post(
      `/api/v1/spaces/${spaceId}/sockets/from-template`, 
      requestData
    );
    return response.data;
  }

  async getSocketsForSpace(spaceId: string): Promise<SocketList> {
    const response: AxiosResponse<SocketList> = await this.client.get(`/api/v1/spaces/${spaceId}/sockets`);
    return response.data;
  }

  async getSocket(socketId: string): Promise<SpaceSocket> {
    const response: AxiosResponse<SpaceSocket> = await this.client.get(`/api/v1/sockets/${socketId}`);
    return response.data;
  }

  async updateSocket(socketId: string, update: Partial<SpaceSocketCreate>): Promise<SpaceSocket> {
    const response: AxiosResponse<SpaceSocket> = await this.client.put(`/api/v1/sockets/${socketId}`, update);
    return response.data;
  }

  async deleteSocket(socketId: string): Promise<void> {
    await this.client.delete(`/api/v1/sockets/${socketId}`);
  }

  // Integration binding operations
  async createIntegrationBinding(socketId: string, bindingData: IntegrationBindingCreate): Promise<IntegrationBinding> {
    try {
      const response: AxiosResponse<IntegrationBinding> = await this.client.post(
        `/api/v1/sockets/${socketId}/integrations`,
        bindingData
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async getIntegrationBindings(socketId: string): Promise<IntegrationBindingList> {
    const response: AxiosResponse<IntegrationBindingList> = await this.client.get(`/api/v1/sockets/${socketId}/integrations`);
    return response.data;
  }

  async updateIntegrationBinding(socketId: string, bindingId: string, update: IntegrationBindingUpdate): Promise<IntegrationBinding> {
    const response: AxiosResponse<IntegrationBinding> = await this.client.put(`/api/v1/sockets/${socketId}/integrations/${bindingId}`, update);
    return response.data;
  }

  async deleteIntegrationBinding(socketId: string, bindingId: string): Promise<void> {
    await this.client.delete(`/api/v1/sockets/${socketId}/integrations/${bindingId}`);
  }

  async getIntegrationConfig(bindingId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/integrations/${bindingId}/config`);
    return response.data;
  }

  async regenerateMcpCredentials(bindingId: string): Promise<{
    binding_id: string;
    secure_endpoint: string;
    regenerated: boolean;
    api_key?: string;
  }> {
    const response = await this.client.post(
      `/mcp/integrations/${bindingId}/regenerate`
    );
    return response.data;
  }

  // Integration listing operations
  async listIntegrations(spaceId?: string, integrationType?: string, isActive?: boolean): Promise<any[]> {
    const params: any = {};
    if (spaceId) params.space_id = spaceId;
    if (integrationType) params.integration_type = integrationType;
    if (isActive !== undefined) params.is_active = isActive;
    
    try {
      // Try /integrations first (based on router prefix)
      const response = await this.client.get('/integrations', { params });
      return response.data;
    } catch (err: any) {
      // Try /api/v1/integrations as fallback
      try {
        const response = await this.client.get('/api/v1/integrations', { params });
        return response.data;
      } catch (err2: any) {
        throw err2;
      }
    }
  }

  async getRecommendedIntegrations(params?: {
    company_size?: string;
    primary_need?: string;
    limit?: number;
  }): Promise<Array<{ integration_id: string; relevance_score: number; reason: string }>> {
    const queryParams: any = {};
    if (params?.company_size) queryParams.company_size = params.company_size;
    if (params?.primary_need) queryParams.primary_need = params.primary_need;
    if (params?.limit) queryParams.limit = params.limit;
    
    const response = await this.client.get('/api/v1/integrations/recommended', {
      params: queryParams,
      timeout: 60000, // 60 seconds for this endpoint since it can be slow during auth checks
    });
    return response.data;
  }

  // Claude Desktop OAuth operations
  async getClaudeDesktopIntegrations(): Promise<any[]> {
    const response = await this.client.get('/api/v1/mcp/claude-desktop/integrations');
    return response.data;
  }

  // Socket template operations
  async getSocketTemplates(): Promise<any[]> {
    const response = await this.client.get('/api/v1/sockets/templates');
    return response.data;
  }

  async getSocketTemplate(templateId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/sockets/templates/${templateId}`);
    return response.data;
  }

  // Chrome Extension Generation
  async generateExtension(request: ExtensionGenerateRequest): Promise<ExtensionGenerateResponse> {
    try {
      const response = await this.client.post('/api/v1/extensions/generate', request);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Chrome Extension Download (via UI proxy)
  async downloadExtension(downloadId: string): Promise<Blob> {
    const response = await this.client.get(`/api/v1/extensions/download/${downloadId}`, {
      responseType: 'blob' // Important: get binary data
    });
    return response.data;
  }

  // Chrome Extension Info
  async getExtensionInfo(bindingId: string): Promise<{
    status: 'available' | 'expired' | 'not_found';
    message?: string;
    extension_id?: string;
    extension_filename?: string;
    extension_size?: string;
    extension_generated_at?: string;
    extension_download_url?: string;
  }> {
    const response = await this.client.get(`/api/v1/extensions/info/${bindingId}`);
    return response.data;
  }

  // Analytics operations
  async getSearchAnalytics(): Promise<any> {
    const response = await this.client.get('/analytics/search/performance');
    return response.data;
  }

  async getUserActivityFeed(params?: {
    space_id?: string;
    limit?: number;
    offset?: number;
    cursor?: string;
  }): Promise<UserActivityFeedResponse> {
    const response = await this.client.get<UserActivityFeedResponse>(
      '/api/v1/activity',
      { params }
    );
    return response.data;
  }

  async getGamificationState(): Promise<GamificationStateResponse> {
    const response = await this.client.get<GamificationStateResponse>(
      '/api/v1/gamification'
    );
    return response.data;
  }

  async getWeeklyMemoryActivity(spaceId: string): Promise<WeeklyMemoryActivitySummary> {
    const response = await this.client.get<WeeklyMemoryActivitySummary>(
      '/api/v1/activity/weekly-summary',
      { params: { space_id: spaceId } }
    );
    return response.data;
  }

  async getMemoryAnalyticsOverview(
    timeRange: string = '30d',
    spaceId?: string
  ): Promise<any> {
    const params: Record<string, string> = { time_range: timeRange };
    if (spaceId) {
      params.space_id = spaceId;
    }
    const response = await this.client.get('/analytics/memory/overview', {
      params,
    });
    return response.data;
  }

  async getMemoryReadSummary(
    timeRange: MemoryReadTimeRange = '30d',
    spaceId?: string
  ): Promise<MemoryReadSummaryResponse> {
    return this.get<MemoryReadSummaryResponse>(
      '/analytics/memory-reads/summary',
      {
        time_range: timeRange,
        ...(spaceId ? { space_id: spaceId } : {}),
      }
    );
  }

  async getOptimizationRecommendations(): Promise<any[]> {
    return this.get('/analytics/recommendations');
  }

  async getSpaceAnalytics(spaceId: string, timeRange: string = '30d'): Promise<any> {
    return this.get(`/analytics/spaces/${spaceId}`, { time_range: timeRange });
  }

  async getSearchPerformanceAnalytics(timeRange: string = '30d'): Promise<any> {
    return this.get('/analytics/search/performance', { time_range: timeRange });
  }

  async getIntelligenceRecommendations(): Promise<any> {
    return this.get('/analytics/intelligence/recommendations');
  }

  // Generic request methods
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.client.get(url, { params });
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.put(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.client.delete(url, config);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService; 